import { useMemo, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { FileUp, Upload } from 'lucide-react'
import {
  api,
  type RedeemApp,
  type RedeemAppDetail,
  type RedeemBatch,
  type RedeemCode,
  type RedeemCodeState,
  type RedeemImportResult,
} from '@/api/client'
import { useRedeemCodes } from '@/lib/queries'
import { useInvalidateRedeem } from '@/lib/redeem'
import {
  Button,
  ConfirmDialog,
  CopyButton,
  DataTable,
  Field,
  FileButton,
  Input,
  SearchInput,
  Segmented,
  StatusPill,
  Textarea,
  When,
  errorText,
  useToast,
  type Column,
} from '@/ui'
import { Section } from './shared'

/** The codes tab: bring codes in, see which batches are left, find a code. */
export default function AppCodes({ detail }: { detail: RedeemAppDetail }) {
  const { app } = detail
  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <ImportCodes appId={app.id} />
        {app.platform === 'web' && <SharedCodes app={app} />}
      </div>
      <Batches detail={detail} />
      <CodeTable appId={app.id} detail={detail} />
    </div>
  )
}

function ImportCodes({ appId }: { appId: number }) {
  const [csv, setCSV] = useState('')
  const [label, setLabel] = useState('')
  const [expiresOn, setExpiresOn] = useState('')
  const file = useRef<HTMLInputElement>(null)
  const [skipped, setSkipped] = useState<NonNullable<RedeemImportResult['skipped']>>([])
  const invalidate = useInvalidateRedeem()
  const toast = useToast()
  const run = useMutation({
    mutationFn: (fn: () => Promise<RedeemImportResult>) => fn(),
    onSuccess: async (res) => {
      const lost = res.skipped ?? []
      setSkipped(lost)
      // "N줄 중 M개를 읽었다": a line that gave no code is counted, so a
      // file that lost a line does not look complete.
      const read = res.lines ? `${res.lines}줄 중 ${res.rows}개를 읽었다, ${lost.length}줄은 건너뛰었다. ` : `${res.rows}행을 읽었다. `
      toast.success(
        read +
          `새 코드 ${res.added}장` +
          (res.issued > 0 ? `, 그중 ${res.issued}장은 이미 나간 코드` : '') +
          `. 풀에 이미 있던 ${res.duplicates}장은 건너뛰었다.`,
      )
      setCSV('')
      if (file.current) file.current.value = ''
      await invalidate()
    },
  })
  return (
    <Section title="코드 올리기" hint="App Store Connect·Play에서 받은 내보내기를 파일이나 붙여넣기로. 한 줄에 코드 하나, 영문 대문자와 숫자 12자 이상. 코드만 있는 줄, 코드와 주소를 쉼표로 이은 줄이면 된다. 머리글 줄은 읽지 않고 건너뛴다. 건너뛴 줄은 올린 뒤 줄 번호와 이유로 알려 준다.">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="묶음 이름" hint="비우면 파일 이름">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <Field label="묶음 만료일" hint="앱의 만료일보다 우선">
          <Input placeholder="YYYY-MM-DD" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
        </Field>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <FileButton
          ref={file}
          accept=".csv,text/csv"
          inputLabel="CSV 파일"
          icon={FileUp}
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) run.mutate(() => api.importRedeemCSVFile(appId, f, { label: label || f.name, expires_on: expiresOn }))
          }}
        />
        <span className="text-meta text-muted-foreground">파일을 고르면 바로 올라간다.</span>
      </div>
      <Textarea
        className="h-24 font-mono text-meta"
        placeholder="CODE1234567890,https://apps.apple.com/redeem?…"
        value={csv}
        onChange={(e) => setCSV(e.target.value)}
        aria-label="붙여넣은 CSV"
      />
      <Button
        icon={Upload}
        loading={run.isPending}
        disabled={!csv.trim()}
        onClick={() => run.mutate(() => api.importRedeemCodes(appId, { csv, label, expires_on: expiresOn }))}
      >
        붙여넣은 내용 올리기
      </Button>
      {run.error && <p className="text-table text-status-failed">{errorText(run.error)}</p>}
      {skipped.length > 0 && (
        <div role="region" aria-label="건너뛴 줄" className="space-y-2 rounded-md border border-border bg-background p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-table font-medium">건너뛴 줄 {skipped.length}개</p>
            <Button size="sm" variant="ghost" onClick={() => setSkipped([])}>
              닫기
            </Button>
          </div>
          <ul className="max-h-48 divide-y divide-border overflow-auto text-table">
            {skipped.map((l) => (
              <li key={l.line} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1.5">
                <span className="w-16 shrink-0 tabular-nums text-muted-foreground">{l.line}번째 줄</span>
                <code className="min-w-0 flex-1 break-all font-mono text-meta">{l.excerpt}</code>
                <span className="text-meta text-status-failed">{l.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  )
}

/** A web app's shared code: one checkout code, N uses, each DMed to one person
 *  who asks under the post naming it. Adding the same code again adds uses. */
function SharedCodes({ app }: { app: RedeemApp }) {
  const [code, setCode] = useState('')
  const [uses, setUses] = useState(100)
  const [url, setUrl] = useState(app.store_url)
  const invalidate = useInvalidateRedeem()
  const toast = useToast()
  const add = useMutation({
    mutationFn: () => api.addSharedCode(app.id, { code, uses, url, expires_on: app.expires_on }),
    onSuccess: async (res) => {
      toast.success(`${code.toUpperCase()} 사용 횟수 ${res.added}회를 더했다`)
      setCode('')
      await invalidate()
    },
  })
  return (
    <Section title="공유 코드" hint="채널마다 공유 할인 코드 하나. 요청한 사람에게 한 번씩 DM으로 가고, 사용 횟수만큼만 나간다. 남은 횟수는 묶음 표에 있다.">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="코드" hint="결제창에 넣는 코드. 예: SIDEPROJECT20">
          <Input className="font-mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
        </Field>
        <Field label="사용 횟수" hint="쇼핑몰이 이 코드에 둔 상한">
          <Input type="number" min={1} max={1000} value={uses} onChange={(e) => setUses(Number(e.target.value))} />
        </Field>
      </div>
      <Field label="쓰는 곳" hint="DM이 링크하는 페이지">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} />
      </Field>
      {add.error && <p className="text-table text-status-failed">{errorText(add.error)}</p>}
      <Button variant="primary" loading={add.isPending} disabled={!code.trim() || uses < 1} onClick={() => add.mutate()}>
        횟수 더하기
      </Button>
    </Section>
  )
}

function Batches({ detail }: { detail: RedeemAppDetail }) {
  const batches = detail.batches ?? []
  const [recall, setRecall] = useState<RedeemBatch | null>(null)
  const invalidate = useInvalidateRedeem()
  const toast = useToast()
  const set = useMutation({
    mutationFn: (v: { batch: RedeemBatch; state: 'void' | 'available' }) =>
      api.setRedeemBatchState(detail.app.id, v.batch.id, v.state),
    onSuccess: async (r, v) => {
      if (v.state === 'void') toast.success(`남은 코드 ${r.moved}장을 회수했다`)
      else if (r.moved === 0)
        toast.info(
          (r.left_void ?? 0) > 0
            ? `되돌린 코드가 없다. 이 묶음의 무효 ${r.left_void}장은 이 회수로 무효가 된 것인지 알 수 없어 그대로 둔다. 필요한 코드는 코드 목록에서 하나씩 되돌린다.`
            : '되돌릴 코드가 없다',
        )
      else
        toast.success(
          `회수했던 코드 ${r.moved}장을 풀에 되돌렸다` +
            ((r.left_void ?? 0) > 0 ? `. 따로 무효로 한 ${r.left_void}장은 그대로 무효다.` : ''),
        )
      setRecall(null)
      await invalidate()
    },
    onError: (e) => toast.error(errorText(e)),
  })
  if (batches.length === 0) return null
  const columns: Column<RedeemBatch>[] = [
    { key: 'label', header: '묶음', card: 'title', cell: (b) => <span className="font-medium">{b.label || `묶음 ${b.id}`}</span> },
    { key: 'expiry', header: '만료', cell: (b) => b.expires_on || detail.app.expires_on || '—' },
    { key: 'count', header: '코드', align: 'right', cell: (b) => <span className="tabular-nums">{b.code_count}</span> },
    { key: 'left', header: '남음', align: 'right', cell: (b) => <span className="tabular-nums">{b.available}</span> },
    { key: 'at', header: '올린 때', cell: (b) => <When iso={b.created_at} /> },
    {
      key: 'recall',
      header: '',
      align: 'right',
      card: 'meta',
      cell: (b) => {
        const retired = b.available === 0 && b.code_count > 0
        // Switching a pool from one offer to another has to happen in one
        // move: a half-retired pool hands out whichever code the ledger
        // reaches first.
        return retired ? (
          <Button size="sm" disabled={set.isPending} onClick={() => set.mutate({ batch: b, state: 'available' })} title="회수했던 코드를 다시 풀에 넣는다. 나간 코드는 그대로.">
            되돌리기
          </Button>
        ) : (
          <Button size="sm" variant="dangerOutline" disabled={set.isPending} onClick={() => setRecall(b)} title="남은 코드를 전부 회수한다. 나간 코드는 건드리지 않는다.">
            전량 회수
          </Button>
        )
      },
    },
  ]
  return (
    <Section title="묶음" hint="올린 단위. 풀을 다른 제안으로 바꿀 때는 이전 묶음을 한 번에 회수한다.">
      <DataTable label="묶음" columns={columns} rows={batches} rowKey={(b) => b.id} />
      <ConfirmDialog
        open={!!recall}
        onOpenChange={(o) => !o && !set.isPending && setRecall(null)}
        title={`"${recall?.label || `묶음 ${recall?.id}`}"의 남은 코드를 회수할까?`}
        description={`남은 ${recall?.available ?? 0}장이 무효가 된다. 나간 코드는 그대로고, 되돌리기로 풀에 다시 넣을 수 있다.`}
        confirmLabel="전량 회수"
        confirmVariant="destructive"
        loading={set.isPending}
        onConfirm={async () => {
          if (recall) await set.mutateAsync({ batch: recall, state: 'void' })
        }}
      />
    </Section>
  )
}

type StateFilter = RedeemCodeState | ''

function CodeTable({ appId, detail }: { appId: number; detail: RedeemAppDetail }) {
  const [state, setState] = useState<StateFilter>('issued')
  const [search, setSearch] = useState('')
  const q = useRedeemCodes(appId, state)
  const invalidate = useInvalidateRedeem()
  const toast = useToast()
  const flip = useMutation({
    mutationFn: (c: RedeemCode) => api.setRedeemCodeState(c.id, c.state === 'void' ? 'available' : 'void'),
    onSuccess: (_r, c) => {
      toast.success(c.state === 'void' ? `${c.code}을(를) 풀에 되돌렸다` : `${c.code}을(를) 무효로 표시했다`)
      return invalidate()
    },
    onError: (e) => toast.error(errorText(e)),
  })
  const { stats } = detail
  const items = q.data?.items ?? []
  const total = q.data?.total ?? 0
  const needle = search.trim().toLowerCase()
  const rows = useMemo(
    () =>
      needle
        ? items.filter((c) => [c.recipient, c.code, c.note, c.channel].some((v) => v?.toLowerCase().includes(needle)))
        : items,
    [items, needle],
  )

  const expiries = new Set(rows.map((c) => c.expires_on).filter(Boolean))
  const shared = expiries.size === 1 ? [...expiries][0] : undefined
  const columns: Column<RedeemCode>[] = [
    { key: 'code', header: '코드', card: 'title', cell: (c) => <code className="break-all font-mono text-meta">{c.code}</code> },
    {
      key: 'state',
      header: '상태',
      card: 'meta',
      cell: (c) =>
        c.state === 'issued' ? (
          <StatusPill domain="code" status="issued" />
        ) : (
          <button
            type="button"
            disabled={flip.isPending}
            onClick={() => flip.mutate(c)}
            className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            title={c.state === 'void' ? '풀에 되돌린다' : '쓸 수 없는 코드로 표시한다'}
            aria-label={c.state === 'void' ? `${c.code} 풀에 되돌리기` : `${c.code} 무효로 표시`}
          >
            <StatusPill domain="code" status={c.state} />
            <span aria-hidden className="ml-1 text-meta text-muted-foreground">↺</span>
          </button>
        ),
    },
    {
      key: 'to',
      header: '받은 사람',
      cell: (c) => (
        <div>
          {c.recipient || '—'}
          {c.note && <div className="text-meta text-muted-foreground">{c.note}</div>}
        </div>
      ),
    },
    // On the phone a card keeps what differs per code; a blank channel or an
    // expiry every code shares is noise there (the batch table has it).
    {
      key: 'channel',
      header: '채널',
      cardHide: (c) => !c.channel,
      cell: (c) => <span className="text-meta text-muted-foreground">{c.channel || '—'}</span>,
    },
    { key: 'at', header: '나간 때', cardHide: (c) => !c.issued_at, cell: (c) => <When iso={c.issued_at} className="text-meta" /> },
    {
      key: 'exp',
      header: '만료',
      cardHide: (c) => !c.expires_on || c.expires_on === shared,
      cell: (c) => <span className="text-meta">{c.expires_on || '—'}</span>,
    },
    {
      key: 'link',
      header: '링크',
      align: 'right',
      card: 'meta',
      cell: (c) => <CopyButton text={c.redeem_url} what="링크" label="링크" />,
    },
  ]

  return (
    <Section title="코드" hint={`지금 보는 목록 ${total}장 · 사람들이 실제로 누르는 건 링크다`}>
      <div className="flex flex-wrap items-center gap-2">
        <Segmented<StateFilter>
          label="코드 상태"
          value={state}
          onChange={setState}
          options={[
            { value: '', label: '전체', count: stats.total },
            { value: 'issued', label: '나감', count: stats.issued },
            { value: 'available', label: '남음', count: stats.available },
            { value: 'void', label: '무효', count: stats.void },
          ]}
        />
        <SearchInput value={search} onChange={setSearch} placeholder="받은 사람·코드 찾기" className="ml-auto" />
      </div>
      <DataTable
        label="코드 목록"
        columns={columns}
        rows={q.data ? rows : undefined}
        loading={q.isPending}
        error={q.error && !q.data ? q.error : undefined}
        onRetry={() => q.refetch()}
        rowKey={(c) => c.id}
        empty={<p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-table text-muted-foreground">{needle ? '찾는 코드가 없다' : '이 상태의 코드가 없다'}</p>}
      />
      {total > items.length && (
        <p className="text-meta text-muted-foreground">앞의 {items.length}장만 불러왔다. 나머지는 상태를 좁혀서 본다.</p>
      )}
    </Section>
  )
}
